import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Product, Category, SpecialOffer, StoreInfo } from "./types";
import { dataService } from "./services/dataService";
import { StoreInfoContext } from "./contexts/StoreInfoContext";
import { getEffectiveFeatureFlags } from "./utils/featureFlags";
import Layout from "./components/Layout";
import PlayfulLoader from "./components/PlayfulLoader";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import CategoryPage from "./pages/CategoryPage";
import CategoriesPage from "./pages/CategoriesPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import OffersPage from "./pages/OffersPage";
import VideosPage from "./pages/VideosPage";
import ContactPage from "./pages/ContactPage";

function App() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [specialOffers, setSpecialOffers] = useState<SpecialOffer[]>([]);
  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get feature flags
  const featureFlags = getEffectiveFeatureFlags();

  // Reads never wait on device registration: a visitor whose registration
  // hangs or fails still gets the store.
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const [
          categoriesResponse,
          featuredProductsResponse,
          specialOffersResponse,
          storeInfoResponse,
        ] = await Promise.all([
          dataService.getCategories(),
          dataService.getFeaturedProducts(),
          dataService.getSpecialOffers(),
          dataService.getStoreInfo(),
        ]);

        if (categoriesResponse.success) setCategories(categoriesResponse.data!);
        if (featuredProductsResponse.success)
          setFeaturedProducts(featuredProductsResponse.data!);
        if (specialOffersResponse.success)
          setSpecialOffers(specialOffersResponse.data!);
        if (storeInfoResponse.success) setStoreInfo(storeInfoResponse.data!);
      } catch (error) {
        // Silently handle errors - don't show error screens to users
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  if (isLoading) {
    return (
      <PlayfulLoader
        size="lg"
        variant="toys"
        message="🎁 Loading amazing toys for you..."
        fullScreen
      />
    );
  }

  // A failed read leaves its section empty rather than showing an error screen.
  const effectiveStoreInfo: StoreInfo = storeInfo || {
    name: "Benipal Toys",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    phone: "",
    phone2: "",
    email: "",
    hours: {
      weekdays: "",
      weekends: "",
    },
    socialMedia: {
      instagram: "",
      youtube: "",
      whatsappChannel: "",
    },
  };

  return (
    <StoreInfoContext.Provider value={effectiveStoreInfo}>
      <Routes>
        <Route path="/" element={<Layout storeInfo={effectiveStoreInfo} />}>
          <Route
            index
            element={
              <HomePage
                categories={categories}
                featuredProducts={featuredProducts}
                specialOffers={specialOffers}
                storeInfo={effectiveStoreInfo}
              />
            }
          />
          {/* Categories route - Always available */}
          <Route path="categories" element={<CategoriesPage />} />

          {/* Category route - Always available so categories are clickable */}
          <Route path="category/:categoryId" element={<CategoryPage />} />

          {/* Products routes - Always available, feature flag controls visibility in UI */}
          <Route path="products" element={<ProductsPage />} />
          <Route path="product/:productId" element={<ProductDetailPage />} />

          {/* Offers route - Only available if feature flag is enabled */}
          {featureFlags.showOffers && (
            <Route path="offers" element={<OffersPage />} />
          )}

          {/* Videos route - Only available if feature flag is enabled */}
          {featureFlags.showVideos && (
            <Route path="videos" element={<VideosPage />} />
          )}
          <Route
            path="contact"
            element={<ContactPage storeInfo={effectiveStoreInfo} />}
          />
          {/* Catch-all route - redirect any unhandled URLs to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </StoreInfoContext.Provider>
  );
}

export default App;
